import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import logo from "@/assets/logo.webp";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
  head: () => ({
    meta: [
      { title: "Reset password — Business Dite Ads" },
      { name: "description", content: "Set a new password for your Business Dite Ads account." },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://busditeads.com/reset-password" }],
  }),
});

function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        setReady(true);
      }
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error(t("resetPassword.tooShortTitle"), {
        description: t("resetPassword.tooShortDesc"),
      });
      return;
    }
    if (password !== confirm) {
      toast.error(t("resetPassword.noMatch"));
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (error) {
      toast.error(t("resetPassword.updateFailed"), { description: error.message });
      return;
    }
    toast.success(t("resetPassword.successTitle"), { description: t("resetPassword.successDesc") });
    navigate({ to: "/dashboard" });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-40"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 0%, color-mix(in oklab, var(--primary) 30%, transparent), transparent 70%)",
        }}
      />
      <div className="w-full max-w-md">
        <Link to="/" className="mb-6 flex items-center justify-center gap-2">
          <img src={logo} alt="Business Dite Ads" className="h-10 w-10 object-contain" />
          <span className="text-base font-semibold tracking-tight">
            Business <span className="text-primary">Dite Ads</span>
          </span>
        </Link>

        <Card className="p-6 shadow-[var(--shadow-soft)]">
          <h1 className="text-lg font-semibold tracking-tight">{t("resetPassword.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {ready ? t("resetPassword.ready") : t("resetPassword.validating")}
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-password">{t("resetPassword.newPassword")}</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                disabled={!ready}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">{t("resetPassword.confirmPassword")}</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                disabled={!ready}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{t("resetPassword.minChars")}</p>
            </div>
            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={!ready || submitting}
            >
              {submitting ? t("resetPassword.saving") : t("resetPassword.save")}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/auth" className="text-xs text-muted-foreground hover:underline">
              {t("resetPassword.backToLogin")}
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
